export const RAW_GPS_GEOMETRY_PROVIDER = "raw_gps";
export const DEFAULT_GAP_POLICY = Object.freeze({
  maximumIntervalMs: 120000,
  maximumDistanceMeters: 3000,
  maximumImpliedSpeedKph: 160,
  stationarySpeedThresholdKph: 1,
  stationaryDriftToleranceMeters: 25,
});

export const EDGE_CLASSIFICATIONS = Object.freeze({
  RELIABLE_MOVEMENT: "reliable_movement",
  STATIONARY_DWELL: "stationary_dwell",
  MOVEMENT_GAP: "movement_gap",
  INVALID: "invalid",
});

const EARTH_RADIUS_METERS = 6371008.8;
const radians = (degrees) => degrees * Math.PI / 180;

export function distanceMeters(left, right) {
  const latitudeDelta = radians(right.latitude - left.latitude);
  const longitudeDelta = radians(right.longitude - left.longitude);
  const leftLatitude = radians(left.latitude);
  const rightLatitude = radians(right.latitude);
  const value = Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(leftLatitude) * Math.cos(rightLatitude) * Math.sin(longitudeDelta / 2) ** 2;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(value)));
}

function resolvePolicy(policy) {
  const resolved = Object.freeze({ ...DEFAULT_GAP_POLICY, ...policy });
  if (!Number.isFinite(resolved.maximumIntervalMs) || resolved.maximumIntervalMs < 1 ||
      !Number.isFinite(resolved.maximumDistanceMeters) || resolved.maximumDistanceMeters < 1 ||
      !Number.isFinite(resolved.maximumImpliedSpeedKph) || resolved.maximumImpliedSpeedKph < 1 ||
      !Number.isFinite(resolved.stationarySpeedThresholdKph) || resolved.stationarySpeedThresholdKph < 0 ||
      !Number.isFinite(resolved.stationaryDriftToleranceMeters) || resolved.stationaryDriftToleranceMeters < 0) {
    throw new TypeError("GPS replay geometry policy is invalid.");
  }
  return resolved;
}

function classifyEdge({ elapsedMs, distance, impliedSpeedKph, current, next }, policy) {
  if (current.outOfOrder || next.outOfOrder || elapsedMs <= 0) {
    return { classification: EDGE_CLASSIFICATIONS.INVALID, reason: "timestamp_or_order" };
  }
  const endpointsStationary = current.speedKph <= policy.stationarySpeedThresholdKph &&
    next.speedKph <= policy.stationarySpeedThresholdKph;
  if (endpointsStationary && distance <= policy.stationaryDriftToleranceMeters) {
    return { classification: EDGE_CLASSIFICATIONS.STATIONARY_DWELL, reason: "stationary_drift" };
  }
  if (endpointsStationary) {
    return { classification: EDGE_CLASSIFICATIONS.MOVEMENT_GAP, reason: "stationary_displacement_conflict" };
  }
  if (elapsedMs > policy.maximumIntervalMs) {
    return { classification: EDGE_CLASSIFICATIONS.MOVEMENT_GAP, reason: "moving_time_gap" };
  }
  if (distance > policy.maximumDistanceMeters) {
    return { classification: EDGE_CLASSIFICATIONS.MOVEMENT_GAP, reason: "distance_gap" };
  }
  if (impliedSpeedKph > policy.maximumImpliedSpeedKph) {
    return { classification: EDGE_CLASSIFICATIONS.MOVEMENT_GAP, reason: "implausible_jump" };
  }
  return { classification: EDGE_CLASSIFICATIONS.RELIABLE_MOVEMENT, reason: null };
}

export function buildRawGpsGeometry(observations, policy = DEFAULT_GAP_POLICY) {
  if (!Array.isArray(observations) || !policy) {
    throw new TypeError("GPS replay geometry input is invalid.");
  }
  const resolvedPolicy = resolvePolicy(policy);
  if (!observations.length) {
    return Object.freeze({
      provider: RAW_GPS_GEOMETRY_PROVIDER,
      policy: resolvedPolicy,
      observations: Object.freeze([]),
      segments: Object.freeze([]),
      edges: Object.freeze([]),
      gaps: Object.freeze([]),
      dwells: Object.freeze([]),
      invalidEdges: Object.freeze([]),
      reliableEdges: Object.freeze([]),
    });
  }

  const segments = [];
  const edges = [];
  const gaps = [];
  const dwells = [];
  const invalidEdges = [];
  const reliableEdges = [];
  let points = [observations[0]];
  for (let index = 0; index < observations.length - 1; index += 1) {
    const current = observations[index];
    const next = observations[index + 1];
    const elapsedMs = next.recordedAt.getTime() - current.recordedAt.getTime();
    const distance = distanceMeters(current.coordinates, next.coordinates);
    const impliedSpeedKph = elapsedMs > 0 ? distance / elapsedMs * 3600 : Infinity;
    const result = classifyEdge(
      { elapsedMs, distance, impliedSpeedKph, current, next }, resolvedPolicy
    );
    const edge = Object.freeze({
      fromIndex: index,
      toIndex: index + 1,
      from: current,
      to: next,
      durationMs: Math.max(0, elapsedMs),
      distanceMeters: distance,
      impliedSpeedKph,
      ...result,
    });
    edges.push(edge);
    reliableEdges.push(result.classification === EDGE_CLASSIFICATIONS.RELIABLE_MOVEMENT);
    if (result.classification !== EDGE_CLASSIFICATIONS.RELIABLE_MOVEMENT) {
      segments.push(Object.freeze({
        id: `raw-${segments.length}`,
        provider: RAW_GPS_GEOMETRY_PROVIDER,
        points: Object.freeze(points),
      }));
      if (result.classification === EDGE_CLASSIFICATIONS.STATIONARY_DWELL) dwells.push(edge);
      else if (result.classification === EDGE_CLASSIFICATIONS.MOVEMENT_GAP) gaps.push(edge);
      else invalidEdges.push(edge);
      points = [next];
    } else {
      points.push(next);
    }
  }
  segments.push(Object.freeze({
    id: `raw-${segments.length}`,
    provider: RAW_GPS_GEOMETRY_PROVIDER,
    points: Object.freeze(points),
  }));
  return Object.freeze({
    provider: RAW_GPS_GEOMETRY_PROVIDER,
    policy: resolvedPolicy,
    observations: Object.freeze([...observations]),
    segments: Object.freeze(segments),
    edges: Object.freeze(edges),
    gaps: Object.freeze(gaps),
    dwells: Object.freeze(dwells),
    invalidEdges: Object.freeze(invalidEdges),
    reliableEdges: Object.freeze(reliableEdges),
  });
}

export function summarizeRouteCoverage(geometry) {
  const observations = geometry?.observations || [];
  const gaps = geometry?.gaps || [];
  const invalidEdges = geometry?.invalidEdges || [];
  const startMs = observations[0]?.recordedAt.getTime() ?? null;
  const endMs = observations.at(-1)?.recordedAt.getTime() ?? null;
  const durationMs = startMs === null || endMs === null ? 0 : Math.max(0, endMs - startMs);
  const observationDensityPerHour = durationMs > 0
    ? Math.round((Math.max(0, observations.length - 1) * 3600000 / durationMs) * 10) / 10
    : null;
  const unknownTravelDurationMs = [...gaps, ...invalidEdges]
    .reduce((total, edge) => total + edge.durationMs, 0);
  const maximumIntervalMs = geometry?.policy?.maximumIntervalMs ?? DEFAULT_GAP_POLICY.maximumIntervalMs;
  const movingTimestampGapCount = gaps.filter((edge) => edge.durationMs > maximumIntervalMs).length;
  const spatialGapCount = gaps.filter((edge) => [
    "stationary_displacement_conflict", "distance_gap", "implausible_jump",
  ].includes(edge.reason)).length;
  let classification = "complete_observation_timeline";
  if (observations.length < 2) classification = "insufficient_observations";
  else if (gaps.length || invalidEdges.length) classification = "partial_observation_timeline";
  return Object.freeze({
    classification,
    observationDensityPerHour,
    movingTimestampGapCount,
    spatialGapCount,
    unknownTravelDurationMs,
    invalidEdgeCount: invalidEdges.length,
  });
}
